import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useGetLoanApplicationByTask, useRecordDisbursement, useUploadDocument, cancelLoanApplication } from '../../../services/loanService';
import { downloadDocument } from '../../../services/documentService';
import { LoanApplication } from '../../../services/schemas/loanServiceSchemas';
import { UseQueryOptions } from '@tanstack/react-query';
import { DisbursementStatus } from '../../../services/schemas/loanServiceSchemas/disbursementStatus';
import { DocumentReferenceDocumentType } from '../../../services/schemas/loanServiceSchemas/documentReferenceDocumentType';
import { toLocalDateString, toLocalDateTimeInputValue } from '../../../utils/localDate';
import ApprovalConditionsCard from '../../common/ApprovalConditionsCard';
import { DocumentPreviewModal, useDocumentPreview } from '../../common/DocumentPreview';

interface DisbursementDetails {
    referenceNumber: string;
    disbursementDate: string;
    notes: string;
    disbursementAmount?: number;
    // Who the money was paid to: prefilled from the client's bank account, editable.
    payeeName: string;
    payeeBank: string;
    payeeAccountNumber: string;
    payeeBranchCode: string;
}

const DisbursementDetails = () => {
    const { taskId } = useParams<{ taskId: string }>();
    const navigate = useNavigate();
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [isError, setIsError] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [loanApplicationId, setLoanApplicationId] = useState<string | undefined>();
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelReason, setCancelReason] = useState('');
    const [isCancelling, setIsCancelling] = useState(false);
    const [disbursement, setDisbursement] = useState<DisbursementDetails>({
        referenceNumber: '',
        disbursementDate: toLocalDateTimeInputValue(), // Format: YYYY-MM-DDTHH:mm
        notes: '',
        disbursementAmount: undefined,
        payeeName: '',
        payeeBank: '',
        payeeAccountNumber: '',
        payeeBranchCode: ''
    });

    // Add state for loan agreement document ID
    const [loanAgreementId, setLoanAgreementId] = useState<string | null>(null);

    // Debug useEffect to track taskId changes
    useEffect(() => {
        console.log('Component mounted with taskId:', taskId);
        if (!taskId) {
            console.error('No taskId provided in URL');
            toast.error('Invalid task ID');
            navigate('/work-items');
        }
    }, [taskId, navigate]);

    // Fetch loan application by task ID
    const {
        data: loanApplication,
        isLoading: isLoadingApplication,
        error: applicationError,
        refetch: refetchLoanApplication
    } = useGetLoanApplicationByTask(
        taskId || '',
        {
            query: {
                enabled: !!taskId,
                onSuccess: (data: LoanApplication) => {
                    console.log('Loan application fetched successfully:', data);
                    if (data?.id) {
                        console.log('Setting loan application ID:', data.id);
                        setLoanApplicationId(data.id);
                    } else {
                        console.error('No loan application ID found in response');
                        toast.error('Failed to load application: No application ID found');
                    }
                },
                onError: (error: Error) => {
                    console.error('Error fetching loan application:', error);
                    toast.error(`Failed to load application: ${error.message}`);
                }
            } as Partial<UseQueryOptions<LoanApplication, Error>>
        }
    );

    // Make sure loanApplicationId is set when loanApplication data is available
    useEffect(() => {
        if (loanApplication && loanApplication.id && !loanApplicationId) {
            console.log('Setting loan application ID from effect:', loanApplication.id);
            setLoanApplicationId(loanApplication.id);
        }
    }, [loanApplication, loanApplicationId]);

    // Debug useEffect to track state changes
    useEffect(() => {
        console.log('State update - taskId:', taskId);
        console.log('State update - loanApplication:', loanApplication);
        console.log('State update - loanApplicationId:', loanApplicationId);
    }, [taskId, loanApplication, loanApplicationId]);

    // Handle application errors
    useEffect(() => {
        if (applicationError) {
            console.error('Error fetching loan application:', applicationError);
            toast.error(`Failed to load application: ${(applicationError as Error).message}`);
        }
    }, [applicationError]);

    // What has already been paid out across earlier tranches, and what is still owed.
    //
    // A loan can be disbursed in parts - R60 000 now, R40 000 in two days - so the amount to
    // pre-fill is the REMAINING balance, not the full approved amount. Pre-filling the full
    // amount on a second tranche would be rejected by the over-disbursement guard, and if it
    // were not, it would pay the client R160 000 on a R100 000 loan.
    const tranches = ((loanApplication as any)?.disbursements ?? []) as { disbursementAmount?: number; disbursementDate?: string }[];
    const approvedAmount = loanApplication?.loanDetails?.loanAmount ?? 0;
    const alreadyDisbursed = tranches.reduce((sum, t) => sum + (t.disbursementAmount ?? 0), 0);
    const retainedPricing = loanApplication as (LoanApplication & {
        individualPricingQuoteId?: string; individualPricingQuoteSnapshot?: string;
    }) | undefined;
    const quotedPayout = useMemo(() => {
        if (loanApplication?.loanType !== 'PERSONAL' ||
            (!retainedPricing?.individualPricingQuoteId && !retainedPricing?.individualPricingQuoteSnapshot)) return null;
        try {
            const quote = JSON.parse(retainedPricing?.individualPricingQuoteSnapshot || '');
            const amount = quote.funding?.cashReceived;
            const date = quote.inputs?.advanceDate;
            if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0 ||
                typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) throw new Error('Invalid payout terms');
            return { amount, date, error: '' };
        } catch {
            return { amount: undefined, date: undefined,
                error: 'The retained payout terms could not be read. Reload the application before recording disbursement.' };
        }
    }, [loanApplication?.loanType, retainedPricing?.individualPricingQuoteId, retainedPricing?.individualPricingQuoteSnapshot]);
    const payoutMismatch = !quotedPayout ? '' : quotedPayout.error || (tranches.length > 0
        ? 'This quoted individual loan already has a recorded payout. Review its existing disbursement.'
        : disbursement.disbursementDate.slice(0, 10) !== quotedPayout.date
            ? `The payment date must match the quotation’s advance date (${quotedPayout.date}). Record the actual payment date. If it differs, obtain revised and approved terms for that date before proceeding.`
            : Number(disbursement.disbursementAmount) !== quotedPayout.amount
                ? `The payout must match the quoted cash received: R ${quotedPayout.amount?.toFixed(2)}.` : '');
    // Conditions the committee set to be met before disbursement. They warn rather than block: the
    // business may pay out with some outstanding, giving a reason that is kept on the disbursement.
    // Waiving one (from the card above) takes it off this list.
    const outstandingConditions = (loanApplication?.approvalConditions || []).filter(
        condition => condition.status === 'OUTSTANDING' && condition.conditionType === 'BEFORE_DISBURSEMENT'
    );
    // Nothing is paid out while a condition is outstanding (the business, 23 Oct 2026): fulfil or waive it first
    const conditionsOutstanding = outstandingConditions.length > 0;

    // The bank's proof of payment: uploaded with the disbursement so the payout is evidenced on the file.
    const [proofOfPayment, setProofOfPayment] = useState<File | null>(null);
    const uploadDocumentMutation = useUploadDocument();

    // Prefill the payee from the client's bank account, without overwriting anything typed
    useEffect(() => {
        const account = loanApplication?.bankAccount;
        if (!account) return;
        setDisbursement(prev => ({
            ...prev,
            payeeName: prev.payeeName || account.accountHolderName || '',
            payeeBank: prev.payeeBank || account.bankName || '',
            payeeAccountNumber: prev.payeeAccountNumber || account.accountNumber || '',
            payeeBranchCode: prev.payeeBranchCode || account.branchCode || ''
        }));
    }, [loanApplication?.bankAccount]);

    const remainingToDisburse = Math.max(0, Math.round(((quotedPayout?.amount ?? approvedAmount) - alreadyDisbursed) * 100) / 100);

    useEffect(() => {
        if (approvedAmount > 0) {
            setDisbursement(prev => ({
                ...prev,
                disbursementAmount: remainingToDisburse
            }));
        }
    }, [approvedAmount, remainingToDisburse]);

    // Proof of banking, uploaded at Upload Documents and checked at verification (#33)
    const proofOfBanking = (loanApplication?.documents || []).find(
        doc => doc.documentType === DocumentReferenceDocumentType.BANK_LETTER && doc.status !== 'SUPERSEDED'
    );
    // Shown in the page: the document service sends no usable content type, so a plain blob link downloaded a nameless file
    const documentPreview = useDocumentPreview(() => toast.error('The proof of banking could not be opened'));
    const viewProofOfBanking = () => {
        if (proofOfBanking?.documentKey) documentPreview.open(proofOfBanking.documentKey, proofOfBanking.name || 'Proof of banking');
    };

    // Function to handle loan agreement download
    const handleLoanAgreementDownload = async () => {
        if (!loanApplication?.documents) {
            toast.error('No documents found');
            return;
        }

        const loanAgreement = loanApplication.documents.find(
            doc => doc.documentType === DocumentReferenceDocumentType.LOAN_AGREEMENT
        );

        if (!loanAgreement?.documentKey) {
            toast.error('Loan agreement document not found');
            return;
        }

        try {
            const blob = await downloadDocument(loanAgreement.documentKey);

            // Generate a proper document name
            const customerName = loanApplication.loanType === 'BUSINESS'
                ? loanApplication.company?.companyName
                : loanApplication.individual?.fullNames;
            const applicationNumber = loanApplication.applicationNumber;
            const date = toLocalDateString(); // YYYY-MM-DD
            const documentName = `Loan_Agreement_${customerName}_${applicationNumber}_${date}.pdf`
                .replace(/[^a-zA-Z0-9._-]/g, '_'); // Sanitize filename

            // Create a blob from the response
            const url = window.URL.createObjectURL(blob);

            // Create a temporary link element
            const link = document.createElement('a');
            link.href = url;
            link.download = documentName;

            // Append to body, click and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading document:', error);
            toast.error('Failed to download loan agreement');
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setErrorMessage('');
        setDisbursement(prev => ({
            ...prev,
            [name]: name === 'disbursementAmount' ? (value === '' ? undefined : Number(value)) : value
        }));
    };

    // Add mutation for recording disbursement
    const recordDisbursementMutation = useRecordDisbursement({
        mutation: {
            onSuccess: () => {
                console.log('Disbursement recorded successfully');
                toast.success('Disbursement recorded successfully');
                setIsSubmitted(true);
                refetchLoanApplication();
            },
            onError: (error: Error) => {
                console.error('Error recording disbursement:', error);
                toast.error(`Failed to record disbursement: ${(error as any)?.response?.data?.message || error.message}`);
            }
        }
    });

    const handleCancelApplication = async () => {
        if (!cancelReason.trim()) {
            toast.error('Please provide a reason for cancellation');
            return;
        }
        const appId = loanApplicationId;
        if (!appId) {
            toast.error('No application ID available');
            return;
        }
        setIsCancelling(true);
        try {
            await cancelLoanApplication(appId, { reason: cancelReason.trim() });
            toast.success('Application cancelled successfully');
            setShowCancelModal(false);
            setCancelReason('');
            navigate('/applications');
        } catch (err: any) {
            const message = err?.response?.data?.message || err?.message || 'Failed to cancel application';
            toast.error(message);
        } finally {
            setIsCancelling(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        console.log("Starting handle submit...");
        console.log("Current taskId:", taskId);
        console.log("Current loanApplicationId:", loanApplicationId);
        e.preventDefault();
        setIsError(false);
        setErrorMessage('');

        if (!loanApplicationId) {
            console.error('No loan application ID available');
            toast.error('Application ID not found. Please try refreshing the page.');
            return;
        }

        // Check retained terms before uploading evidence or attempting a payout.
        if (payoutMismatch) {
            setErrorMessage(payoutMismatch);
            return;
        }

        // Format the date with timezone offset
        const date = new Date(disbursement.disbursementDate);
        const timezoneOffset = date.getTimezoneOffset();
        const hours = Math.abs(Math.floor(timezoneOffset / 60)).toString().padStart(2, '0');
        const minutes = Math.abs(timezoneOffset % 60).toString().padStart(2, '0');
        const sign = timezoneOffset <= 0 ? '+' : '-';
        const formattedDate = `${disbursement.disbursementDate}:00${sign}${hours}:${minutes}`;

        if (!disbursement.payeeName.trim() || !disbursement.payeeAccountNumber.trim()) {
            toast.error("Capture the payee's name and account number");
            return;
        }
        // Proof of payment, when the bank has already sent it: uploaded first so the disbursement
        // records which document evidences it. Otherwise it is attached later, from the loan.
        let proofOfPaymentDocumentKey: string | undefined;
        if (proofOfPayment) try {
            const uploaded = await uploadDocumentMutation.mutateAsync({
                loanApplicationId,
                data: {
                    file: proofOfPayment,
                    documentType: 'PROOF_OF_PAYMENT',
                    description: `Proof of payment${disbursement.referenceNumber ? ` - ${disbursement.referenceNumber}` : ''}`
                }
            });
            proofOfPaymentDocumentKey = uploaded?.documentKey;
        } catch (error) {
            console.error('Error uploading proof of payment:', error);
            toast.error('Failed to upload the proof of payment');
            return;
        }
        if (proofOfPayment && !proofOfPaymentDocumentKey) {
            toast.error('The proof of payment was not stored. Please try again.');
            return;
        }

        const submissionData = {
            applicationId: loanApplicationId,
            data: {
                loanApplicationId: loanApplicationId,
                disbursementAmount: disbursement.disbursementAmount ?? remainingToDisburse,
                disbursementDate: formattedDate,
                bankReference: disbursement.referenceNumber,
                payeeName: disbursement.payeeName.trim(),
                payeeBank: disbursement.payeeBank.trim() || undefined,
                payeeAccountNumber: disbursement.payeeAccountNumber.trim(),
                payeeBranchCode: disbursement.payeeBranchCode.trim() || undefined,
                proofOfPaymentDocumentKey,
                notes: disbursement.notes.trim() || undefined,
                status: DisbursementStatus.COMPLETED
            }
        };

        console.log("Submitting with data:", submissionData);

        try {
            await recordDisbursementMutation.mutateAsync(submissionData);
            console.log("Disbursement recorded successfully");
        } catch (error: any) {
            console.error('Error recording disbursement:', error);
            setIsError(true);
            setErrorMessage(error?.response?.data?.message || error?.message || 'Failed to record disbursement');
            toast.error('Failed to record disbursement');
        }
    };

    if (isError) {
        return (
            <div className="container-fluid p-4">
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <div className="mb-4">
                            <div className="bg-danger bg-opacity-10 rounded-circle p-4 d-inline-block">
                                <i className="bi bi-x-circle text-danger" style={{ fontSize: '4rem' }}></i>
                            </div>
                        </div>
                        <h2 className="mb-3 fw-bold">Disbursement Failed</h2>
                        <p className="text-muted mb-4 fs-5">{errorMessage}</p>
                        <div className="d-flex justify-content-center gap-3">
                            <button
                                className="btn btn-primary btn-lg px-4"
                                onClick={() => setIsError(false)}
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Try Again
                            </button>
                            <button
                                className="btn btn-outline-primary btn-lg px-4"
                                onClick={() => navigate('/work-items')}
                            >
                                <i className="bi bi-house me-2"></i>
                                Return to Work Items
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isSubmitted) {
        return (
            <div className="container-fluid p-4">
                <div className="card border-0 shadow-sm">
                    <div className="card-body text-center py-5">
                        <div className="mb-4">
                            <div className="bg-success bg-opacity-10 rounded-circle p-4 d-inline-block">
                                <i className="bi bi-check-circle text-success" style={{ fontSize: '4rem' }}></i>
                            </div>
                        </div>
                        <h2 className="mb-3 fw-bold">Disbursement Recorded Successfully</h2>
                        <p className="text-muted mb-4 fs-5">The loan disbursement has been recorded and processed successfully.</p>
                        <div className="d-flex justify-content-center gap-3">
                            <Link
                                to={`/application/${loanApplication?.id}`}
                                className="btn btn-primary btn-lg px-4"
                            >
                                <i className="bi bi-eye me-2"></i>
                                View Loan Details
                            </Link>
                            <button
                                className="btn btn-outline-primary btn-lg px-4"
                                onClick={() => navigate('/work-items')}
                            >
                                <i className="bi bi-arrow-left me-2"></i>
                                Return to Work Items
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (isLoadingApplication) {
        return (
            <div className="container-fluid py-4">
                <div className="card">
                    <div className="card-body text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading application data...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (applicationError) {
        return (
            <div className="container-fluid py-4">
                <div className="card">
                    <div className="card-body">
                        <div className="alert alert-danger d-flex align-items-center" role="alert">
                            <i className="bi bi-exclamation-triangle-fill me-2"></i>
                            <div>Failed to load application: {(applicationError as Error).message}</div>
                        </div>
                        <div className="text-center mt-4">
                            <Link to="/work-items" className="btn btn-outline-primary">
                                <i className="bi bi-arrow-left me-2"></i>Back to Work Items
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
                <div className="container-fluid">
                    <div className="row g-3 mb-3 align-items-center">
                        <div className="col">
                            <ol className="breadcrumb bg-transparent mb-0">
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/">Home</Link></li>
                                <li className="breadcrumb-item"><Link className="text-secondary" to="/work-items">Work Items</Link></li>
                                <li className="breadcrumb-item active" aria-current="page">Record Disbursement</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header d-flex justify-content-between align-items-center">
                                    <h6 className="card-title mb-0">Record Fund Disbursement</h6>
                                    <div className="d-flex gap-2">
                                        <Link
                                            to={`/application/${loanApplication?.id}`}
                                            state={{ fromDisbursement: true, taskId }}
                                            className="btn btn-outline-primary"
                                        >
                                            <i className="bi bi-eye me-2"></i>
                                            View Application
                                        </Link>
                                        {loanApplication?.documents?.some(doc => doc.documentType === DocumentReferenceDocumentType.LOAN_AGREEMENT) && (
                                            <button
                                                className="btn btn-outline-primary"
                                                onClick={handleLoanAgreementDownload}
                                            >
                                                <i className="bi bi-file-earmark-text me-2"></i>
                                                View Loan Agreement
                                            </button>
                                        )}
                                        <button
                                            className="btn btn-outline-danger"
                                            onClick={() => setShowCancelModal(true)}
                                        >
                                            <i className="fa fa-times-circle me-1"></i> Cancel Application
                                        </button>
                                    </div>
                                </div>
                                <div className="card-body">
                                    {loanApplication && (
                                        <div className="card border-0 shadow-sm mb-4">
                                            <div className="card-body p-4">
                                                <div className="d-flex justify-content-between align-items-center mb-4">
                                                    <div>
                                                        <h5 className="card-title mb-1 fw-bold">Application Details</h5>
                                                        <p className="text-muted mb-0">Application #{loanApplication.applicationNumber}</p>
                                                    </div>
                                                </div>

                                                <div className="row g-4">
                                                    <div className="col-md-4">
                                                        <div className="p-3 bg-light rounded-3 h-100">
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-white rounded-circle p-2 me-3 shadow-sm">
                                                                    <i className="bi bi-person-circle text-primary fs-4"></i>
                                                                </div>
                                                                <div>
                                                                    <small className="text-muted d-block mb-1">Customer</small>
                                                                    <span className="fw-bold fs-6">
                                                                        {loanApplication.loanType === 'BUSINESS'
                                                                            ? loanApplication.company?.companyName || 'N/A'
                                                                            : loanApplication.individual?.fullNames || 'N/A'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-md-4">
                                                        <div className="p-3 bg-light rounded-3 h-100">
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-white rounded-circle p-2 me-3 shadow-sm">
                                                                    <i className="bi bi-cash-stack text-success fs-4"></i>
                                                                </div>
                                                                <div>
                                                                    <small className="text-muted d-block mb-1">Amount</small>
                                                                    <span className="fw-bold fs-6 text-success">
                                                                        {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(loanApplication.loanDetails?.loanAmount || 0)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="col-md-4">
                                                        <div className="p-3 bg-light rounded-3 h-100">
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-white rounded-circle p-2 me-3 shadow-sm">
                                                                    <i className="bi bi-calendar-event text-warning fs-4"></i>
                                                                </div>
                                                                <div>
                                                                    <small className="text-muted d-block mb-1">Term</small>
                                                                    <span className="fw-bold fs-6">
                                                                        {loanApplication.loanDetails?.numberOfInstallments === 1 ?
                                                                            `${loanApplication.loanDetails?.loanTerms} days` : `${loanApplication.loanDetails?.numberOfInstallments} months`}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/*
                                        Partial disbursement. Shown only once a tranche exists,
                                        so a normal single payout is unchanged. Without this the
                                        person paying out the balance has no way to see what was
                                        already sent, and the amount field looks like the full
                                        loan.
                                    */}
                                    {tranches.length > 0 && (
                                        <div className="alert alert-info d-flex flex-column gap-2 mb-4">
                                            <div className="fw-semibold">
                                                This loan is being paid out in tranches
                                            </div>
                                            <div className="table-responsive">
                                                <table className="table table-sm mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Tranche</th>
                                                            <th>Date</th>
                                                            <th className="text-end">Amount</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {tranches.map((tranche, index) => (
                                                            <tr key={index}>
                                                                <td>{index + 1}</td>
                                                                <td>{tranche.disbursementDate ? String(tranche.disbursementDate).split('T')[0] : '—'}</td>
                                                                <td className="text-end">
                                                                    {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(tranche.disbursementAmount ?? 0)}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="fw-semibold">
                                                            <td colSpan={2}>Paid out so far</td>
                                                            <td className="text-end">
                                                                {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(alreadyDisbursed)}
                                                            </td>
                                                        </tr>
                                                        <tr className="fw-semibold text-primary">
                                                            <td colSpan={2}>Still to pay out</td>
                                                            <td className="text-end">
                                                                {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(remainingToDisburse)}
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                            <small className="text-muted">
                                                The admin fee and upfront interest were charged on the
                                                first tranche and are not charged again.
                                            </small>
                                        </div>
                                    )}

                                    <ApprovalConditionsCard
                                        applicationId={loanApplication?.id}
                                        onChanged={() => refetchLoanApplication()}
                                    />

                                    <form onSubmit={handleSubmit}>
                                        <div className="row g-3">
                                            <div className="col-12">
                                                <h6 className="mb-1">Payee</h6>
                                                <small className="text-muted">Prefilled from the client's bank account; change it if the funds go elsewhere.</small>
                                            </div>
                                            <div className="col-12">
                                                {proofOfBanking ? (
                                                    <div className="alert alert-light border d-flex flex-wrap align-items-center gap-2 mb-0 py-2">
                                                        <i className="fa fa-university text-success"></i>
                                                        <span>Proof of banking on file: <strong>{proofOfBanking.name || 'Bank confirmation letter'}</strong>. Check the payee against it.</span>
                                                        <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={viewProofOfBanking}
                                                            disabled={documentPreview.openingKey === proofOfBanking.documentKey}>View</button>
                                                    </div>
                                                ) : (
                                                    <div className="alert alert-warning mb-0 py-2">
                                                        <i className="fa fa-exclamation-triangle me-2"></i>
                                                        No proof of banking on file. The bank confirmation letter is uploaded at Upload Documents.
                                                    </div>
                                                )}
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="payeeName">Account holder <span className="text-danger">*</span></label>
                                                <input id="payeeName" type="text" className="form-control" name="payeeName"
                                                    value={disbursement.payeeName} onChange={handleInputChange} required />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="payeeBank">Bank</label>
                                                <input id="payeeBank" type="text" className="form-control" name="payeeBank"
                                                    value={disbursement.payeeBank} onChange={handleInputChange} />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="payeeAccountNumber">Account number <span className="text-danger">*</span></label>
                                                <input id="payeeAccountNumber" type="text" className="form-control" name="payeeAccountNumber"
                                                    value={disbursement.payeeAccountNumber} onChange={handleInputChange} required />
                                            </div>
                                            <div className="col-md-6">
                                                <label className="form-label" htmlFor="payeeBranchCode">Branch code</label>
                                                <input id="payeeBranchCode" type="text" className="form-control" name="payeeBranchCode"
                                                    value={disbursement.payeeBranchCode} onChange={handleInputChange} />
                                            </div>

                                            <div className="col-12">
                                                <label className="form-label" htmlFor="proofOfPayment">Proof of payment</label>
                                                <input id="proofOfPayment" type="file" className="form-control" accept=".pdf,.jpg,.jpeg,.png"
                                                    onChange={(e) => setProofOfPayment(e.target.files?.[0] || null)} />
                                                <small className="text-muted">
                                                    The bank's confirmation of this payment, if you have it. If it comes later, attach it from
                                                    the Disbursements card on the application or the loan account.
                                                </small>
                                            </div>

                                            <div className="col-12">
                                                <div className="form-group">
                                                    <label className="form-label">Bank Reference Number</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="referenceNumber"
                                                        value={disbursement.referenceNumber}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter bank transaction reference number"
                                                        required
                                                    />
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="form-group">
                                                    {quotedPayout?.date && <p className="small text-muted">
                                                        Quoted advance date: <strong>{quotedPayout.date}</strong> · Cash to pay: <strong>R {quotedPayout.amount?.toFixed(2)}</strong>
                                                    </p>}
                                                    <label className="form-label" htmlFor="disbursementAmount">Disbursement Amount</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text">R</span>
                                                        <input
                                                            type="number"
                                                            id="disbursementAmount"
                                                            className="form-control"
                                                            name="disbursementAmount"
                                                            value={disbursement.disbursementAmount ?? ''}
                                                            onChange={handleInputChange}
                                                            placeholder="Enter disbursement amount"
                                                            min="0"
                                                            step="0.01"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="form-group">
                                                    <label className="form-label" htmlFor="disbursementDate">Disbursement Date & Time</label>
                                                    <input
                                                        type="datetime-local"
                                                        id="disbursementDate"
                                                        className="form-control"
                                                        name="disbursementDate"
                                                        value={disbursement.disbursementDate}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                    {(payoutMismatch || errorMessage) && <p className="text-danger mt-2" role="alert">{payoutMismatch || errorMessage}</p>}
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                <div className="form-group">
                                                    <label className="form-label">Additional Notes</label>
                                                    <textarea
                                                        className="form-control"
                                                        name="notes"
                                                        rows={3}
                                                        value={disbursement.notes}
                                                        onChange={handleInputChange}
                                                        placeholder="Enter any additional notes about the disbursement"
                                                    ></textarea>
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                {conditionsOutstanding && (
                                                    <div className="alert alert-danger" role="alert">
                                                        <div className="fw-semibold mb-1">
                                                            <i className="fa fa-ban me-2"></i>
                                                            {outstandingConditions.length} approval {outstandingConditions.length === 1 ? 'condition is' : 'conditions are'} still
                                                            outstanding, so this loan cannot be paid out yet.
                                                        </div>
                                                        <ul className="mb-1">
                                                            {outstandingConditions.map(condition => (
                                                                <li key={condition.id || condition.description}>{condition.description}</li>
                                                            ))}
                                                        </ul>
                                                        <small>Mark each one fulfilled, or waive it with a reason, in Approval Conditions above. Then disburse.</small>
                                                    </div>
                                                )}
                                                <button
                                                    type="submit"
                                                    className="btn btn-primary"
                                                    disabled={recordDisbursementMutation.isPending || uploadDocumentMutation.isPending || conditionsOutstanding}
                                                >
                                                    {recordDisbursementMutation.isPending ? (
                                                        <>
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            Recording...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-check-circle me-2"></i>
                                                            Record Disbursement
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cancel Application Modal */}
            {showCancelModal && (
                <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                            <div className="modal-header border-bottom" style={{ padding: '1.5rem 2rem', backgroundColor: '#f8f9fa' }}>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle p-2 me-3" style={{ backgroundColor: '#f8d7da', border: '1px solid rgba(220,53,69,0.3)' }}>
                                        <i className="fa fa-times-circle text-danger" style={{ display: 'block' }}></i>
                                    </div>
                                    <div>
                                        <h5 className="modal-title mb-0 fw-bold">Cancel Application</h5>
                                        <p className="text-muted mb-0 small">This action cannot be undone</p>
                                    </div>
                                </div>
                                <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)} disabled={isCancelling}></button>
                            </div>
                            <div className="modal-body p-4">
                                <p className="text-muted mb-3">Are you sure you want to cancel this loan application? Please provide a reason:</p>
                                <textarea
                                    className="form-control"
                                    rows={3}
                                    value={cancelReason}
                                    onChange={(e) => setCancelReason(e.target.value)}
                                    placeholder="Enter reason for cancellation..."
                                    disabled={isCancelling}
                                    maxLength={500}
                                />
                                <small className="text-muted">{cancelReason.length}/500 characters</small>
                            </div>
                            <div className="modal-footer border-0 px-4 pb-4">
                                <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger rounded-pill px-4"
                                    onClick={handleCancelApplication}
                                    disabled={isCancelling || !cancelReason.trim()}
                                >
                                    {isCancelling ? (
                                        <><span className="spinner-border spinner-border-sm me-2" role="status"></span>Cancelling...</>
                                    ) : (
                                        <><i className="fa fa-times-circle me-2"></i>Cancel Application</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <DocumentPreviewModal preview={documentPreview.preview} onClose={documentPreview.close} />
        </div>
    );
};

export default DisbursementDetails;
